// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { DataCacheToolType } from '@shared/commons/cache/DataCacheToolEnums';
import { TerceraMessageBox } from '../screen/TerceraMessageBox';
import { TerceraChartAction, TerceraChartActionEnum, TerceraChartToolbarsEnum } from '@front/chart/TerceraChartAction';
import { LinkedSystem } from '../misc/LinkedSystem';
import { ModelDataType, TerceraChartMVCCommand } from '@front/chart/TerceraChartMVC';
import { TerceraChartUtils } from '@front/chart/TerceraChartUtils';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { TerceraIndicatorLookupDropDownForm } from '../elements/Lookup/TerceraIndicatorLookupDropDownForm';
import ChartPanelTemplate from '@front/templates/ChartPanelTemplate.html';
import { ControlEvents } from '../elements/Control';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { EditPositionScreen } from '../screen/EditPositionScreen';
import { ModifyOrderScreen } from '../screen/ModifyOrderScreen';
import { TerceraAddOverlayScreen } from '../screen/TerceraAddOverlayScreen';
import { PanelNames, ScreensNames } from '../UtilsClasses/FactoryConstants';
import { LookupDropDownShowParams } from '.././UtilsClasses/LookupDropDownShowParams';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Periods } from '@shared/utils/History/TFInfo';
import { DynProperty } from '@shared/commons/DynProperty';
import { IndicatorManager } from '@shared/commons/cache/indicators/IndicatorManager';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { ITerceraChartShowScreenType } from '@front/chart/ITerceraChartShowScreenType';
import { TerceraChartActionProcessor } from '@front/chart/TerceraChartActionProcessor/TerceraChartActionProcessor';
import { PanelSettingsScreen } from '../screen/PanelSettingsScreen';
import { ChartTradingCore } from '@shared/utils/Trading/ChartTradingCore';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { FullScreenManager } from '../UtilsClasses/FullScreenManager';
import { OrderCancelActionHotkeysEnum, ChartHotkeysEnum } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { DateSelectorButton } from '../elements/Lookup/TerceraDateSelectorDropDown';
import { TerceraWindowBase } from '../screen/TerceraWindowBase';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { BaseChartRactive } from '../elements/TerceraChartRactive/BaseChartRactive';
import { LayersEnum } from '@front/chart/Utils/LayersEnum';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { PropertySetupScreen } from '../screen/PropertySetupScreen';
export class ChartPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.isApplyStyleProperties = false;
        this.terceraChartRactive = null;
        this.chartOE = null;
        this.needUpdateLimitStopNumerics = false;
        this.ModelChanged = new CustomEvent();
        this.myVisualWidget = null;
        this.myTradeNumeric = null; // нумерик для редактирования quantity размещенных на Chart-e ордеров
        this.myTradeNumericPromise = null;
        this.initialTemplateSelected = false;
        this._terceraChart = null;
        this.isOpenedTradingWidget = false;
        this.objectManagerScreen = null;
        this.isChartInit = false;
    }
    getType() {
        return PanelNames.ChartPanel;
    }
    oninit() {
        super.oninit();
        this.indicatorLookupCB = this.indicatorLookupCB.bind(this);
        this.indicatorLookupClosed = this.indicatorLookupClosed.bind(this);
        this.overlayScreenClosed = this.overlayScreenClosed.bind(this);
        this.on('objectManagerButton_btnClick', this.addObjectManagerScreen);
        this.on('indicatorButton_btnClick', this.onIndicatorBtnClick);
        this.on('mouseTradeButton_btnClick', this.mouseTradeButton_btnClick);
        this.observe('instrumentItem', this.onInstrumentItemChanged);
        this.observe('accountItem', this.onAccountItemChanged);
        this.on('timeFrameComboBox_ComboItemClicked', this.onTimeFrameComboBox_ComboItemClicked);
        this.on('drawingStyleComboBox_ComboItemClicked', this.onDrawingStyleComboBox_ComboItemClicked);
        // this.on('loadRangeComboBox_ComboItemClicked', this.onLoadRangeComboBox_ComboItemClicked);
        this.observe('showToolsPanel isShowOE oeSmall', () => __awaiter(this, void 0, void 0, function* () {
            yield this.layoutTable();
        }), { init: false });
        this.observe('showMainToolBarPanel', (newValue) => __awaiter(this, void 0, void 0, function* () {
            yield this.layoutTable();
        }), { init: false });
        this.on('overlay_btnClick', this.overlay_btnClick);
        // TODO
        // Utils.TradingLock.TradingLockChanged.Subscribe(this.updateTradingLockState, this);
        // DataCache.isAllowCache.GlobalWebRulesChanged.Subscribe(this.updateTimeFrameComboBox, this);
        const hideOE = Resources.isHidden('chart.ChartSymbolSelector.oeButton');
        void this.set('showOverlayButton', !Resources.isHidden('chart.ChartSymbolSelector.OverlayTooltip'));
        void this.set('isHiddenMouseTradeButton', !Resources.isHidden('chart.ChartSymbolSelector.mouseTradeButton'));
        void this.set('isHiddenOEButton', !hideOE);
        // У когото может быть открыто ОЕ до его скрытия, нужно его принудительно закрыть
        if (hideOE) {
            void this.set('isShowOE', false);
        }
    }
    oncomplete() {
        const _super = Object.create(null, {
            oncomplete: { get: () => super.oncomplete }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.oncomplete.call(this);
            this.myTradeNumeric = this.Controls.myTradeNumeric;
            this.myVisualWidget = this.Controls.myVisualWidget;
            this.initChart();
            this.chartOE = this.Controls.oe;
            this.OnResize.Subscribe(this.layoutTable, this);
            TradingLockUtils.TradingLock.TradingLockChanged.Subscribe(this.updateTradingLockState, this);
            this.updateTradingLockState();
            this.MouseTradingEnabledSet(false);
            if (!isNullOrUndefined(this.myVisualWidget)) {
                this.myVisualWidget.OnVisualTradingOrderTypeChange.Subscribe((isSingle) => {
                    this.terceraChartRactive.terceraChart.setIsOCOMode(!isSingle);
                }, this);
                this.myVisualWidget.OnIsAllowOTHTradingChanged.Subscribe(() => {
                    const chart = this.GetTerceraChart();
                    if (!isNullOrUndefined(chart)) {
                        chart.IsDirty(LayersEnum.Tools);
                    }
                }, this);
                this.myVisualWidget.Placed = false;
                this.myVisualWidget.showChartSetting = this.ShowScreen.bind(this, ITerceraChartShowScreenType.ChartSettings, { PanelSettingsDefaultPage: DynProperty.VISUAL_TRADING_GROUP });
            }
            // Ugly. http://tp.pfsoft.lan/entity/61292
            // TODO. Implement "Reset layout" feature or whatever it's called.
            this.applyTheme();
            this.deserializeDelayedData();
            if (DataCache.getNumberOfAccounts() === 1) {
                void this.set({ showAccountLookUp: false });
            }
            this.terceraChartRactive.terceraChart.model.ModelItemChanged.Subscribe(() => {
                this.ModelChanged.Raise();
            }, this);
            this.terceraChartRactive.terceraChart.onHasCorrectDataChanged.Subscribe(this.onHasCorrectDataChanged, this);
            // SessionSettings.timeZoneChanged.Subscribe(this.terceraChartRactive.terceraChart.RefreshChart, this.terceraChartRactive.terceraChart);
            KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
            KeyEventProcessor.OnKeyUp.Subscribe(this.onGlobalKeyUp, this);
            this.myToolsPanel = this.Controls.toolsPanel;
            this.myToolsPanel.observe('isVertical', (val) => {
                void this.set('verticalToolsPanel', val);
            });
            this.myToolsPanel.observe('isLeftOrTop', (val) => {
                void this.set('leftOrTopToolsPanel', val);
            });
            this.myToolsPanel.observe('onProcessMove', (moving) => {
                if (!moving) {
                    void this.layoutTable();
                }
            });
            void this.myToolsPanel.set('actionProcessor', this.terceraChartRactive.terceraChart.TerceraChartActionProcessor);
            void this.layoutTable();
            this.addScrollToContainer('.chart-order-entry');
            this.localize();
            this.mouseMoveHandler = this.mouseMoveHandler.bind(this);
            if (!isNullOrUndefined(this.myTradeNumeric)) {
                this.myTradeNumeric.on(ControlEvents.LostFocus, this.onEnterPressed.bind(this));
            }
            const editableList = this.Controls.editableList;
            if (!isNullOrUndefined(editableList)) {
                editableList.set('visible', !Resources.isHidden('chart.templates.visibility'));
                editableList.OnItemSave.Subscribe(this.onSaveTemplate, this);
                editableList.OnSelectList.Subscribe(this.onSelectTemplate, this);
                this.selectInitialTemplate();
            }
            this.initChartTradingCore();
            DataCache.OnHistoryChangedAndNeedReload.Subscribe(this.checkNeedReload, this);
            this.terceraChartRactive.terceraChart.windowsContainer.scrollerRenderer.TurnOffRenderer();
            this.terceraChartRactive.terceraChart.TerceraChartActionProcessor.OnActionProcessor.Subscribe(this.OnActionProcessor, this);
            const isGoToVisible = DataCache.boolRuleValue(RulesSet.IS_ALLOW_MIDDLE_REQUEST_ON_CHART);
            void this.set({ isGoToVisible });
        });
    }
    checkNeedReload(message) {
        var _a;
        const instrument = this.get('instrumentItem');
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const chart = (_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart;
        if (isNullOrUndefined(chart)) {
            return;
        }
        const model = chart.model;
        const tfi = model.GetTimeFrameInfo();
        if (isNullOrUndefined(tfi)) {
            return;
        }
        const route = DataCache.Routes.getRouteById(instrument.Route);
        if (isNullOrUndefined(route)) {
            return;
        }
        const basePeriod = Periods.TranslateToParsePeriod(tfi.Periods);
        const serverPeriod = Periods.TranslateToServerPeriod(basePeriod);
        if (message.instrumentTradableID === instrument.InstrumentTradableID &&
            message.routeID === route.QuoteRouteId &&
            message.barsType === tfi.HistoryType &&
            message.historyPeriod === serverPeriod) {
            chart.RefreshChart();
        }
    }
    initChartTradingCore() {
        const ctc = new ChartTradingCore();
        this.ChartTradingCore = ctc;
        ctc.myTradeNumeric = this.myTradeNumeric;
        ctc.myVisualWidget = this.myVisualWidget;
        ctc.chartOE = this.chartOE;
        ctc.terceraChartRactive = this.terceraChartRactive;
        ctc.TradingNumericErrorChecker = TradingNumericErrorChecker;
        ctc.EditPositionScreen = EditPositionScreen;
        ctc.ModifyOrderScreen = ModifyOrderScreen;
        ctc.TerceraMessageBox = TerceraMessageBox;
        ctc.MainWindowManager = MainWindowManager;
    }
    updateTradingLockState() {
        const tradingLocked = TradingLockUtils.TradingLock.tradingLocked;
        void this.set('tradingLocked', tradingLocked);
        const chart = this.terceraChartRactive.terceraChart;
        if (tradingLocked) {
            const ttRenderer = chart.TerceraChartTradingToolsRenderer;
            this.isOpenedTradingWidget = ttRenderer.MouseTradeEnabled;
            this.MouseTradingEnabledSet(false);
        }
        else {
            this.MouseTradingEnabledSet(this.isOpenedTradingWidget);
        }
        chart.IsDirty(LayersEnum.Tools);
    }
    onInstrumentItemChanged(newIns, oldIns) {
        if (isNullOrUndefined(newIns) || newIns === oldIns) {
            return;
        }
        if (!isNullOrUndefined(this.myVisualWidget)) {
            void this.myVisualWidget.set('instrumentItem', newIns);
        }
        this.symbolLink_Out(false, newIns);
        if (!this.completed) {
            return;
        }
        const chart = !isNullOrUndefined(this.terceraChartRactive) ? this.terceraChartRactive.terceraChart : null;
        if (!isNullOrUndefined(chart)) {
            chart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Instrument), newIns);
            if (!isNullOrUndefined(this.objectManagerScreen)) {
                this.objectManagerScreen.setChartToScreen(chart);
            }
        }
    }
    onAccountItemChanged(newAcc, oldAcc) {
        var _a;
        if (isNullOrUndefined(newAcc) || newAcc === oldAcc) {
            return;
        }
        if (!isNullOrUndefined(this.myVisualWidget)) {
            void this.myVisualWidget.set('accountItem', newAcc);
        }
        if (!isNullOrUndefined(this.myTradeNumeric)) {
            this.myTradeNumeric.settingsUpdate();
        }
        if (!isNullOrUndefined((_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart)) {
            this.terceraChartRactive.terceraChart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Account), newAcc);
        }
        this.accountLink_Out(false, newAcc);
    }
    onEnterPressed() {
        if (isNullOrUndefined(this.myTradeNumeric)) {
            return;
        }
        void this.myTradeNumeric.set('visible', false);
        const value = this.myTradeNumeric.get('value');
        if (!isNullOrUndefined(this.myTradeNumericPromise)) {
            this.myTradeNumericPromise(value);
        }
    }
    repopulate() {
        super.repopulate();
        this.terceraChartRactive.terceraChart.repopulate();
        void this.set({ instrumentItem: this.get('instrumentItem') });
        void this.set({ accountItem: this.get('accountItem') });
    }
    layoutTable() {
        return __awaiter(this, void 0, void 0, function* () {
            const chartRactive = this.terceraChartRactive;
            if (isNullOrUndefined(chartRactive)) {
                return;
            }
            const minSizeForToolBars = this.get('minSizeForToolBars');
            const panelSize = this.getCorrectChartPanelSize();
            if ((panelSize.width <= minSizeForToolBars || panelSize.height <= minSizeForToolBars) &&
                !FullScreenManager.IsFullScreenMode) {
                void this.set({ isChartHasMinSize: true });
            }
            else if (this.get('isChartHasMinSize')) {
                void this.set({ isChartHasMinSize: false });
            }
            yield this.layoutTableResize(chartRactive);
            if (this.get('showToolsPanel')) {
                if (!isNullOrUndefined(this.myToolsPanel)) {
                    this.myToolsPanel.onResize();
                }
            }
            if (!this.isChartInit && chartRactive.terceraChart.width > BaseChartRactive.ZeroSize) {
                chartRactive.terceraChart.RefreshChart();
                this.isChartInit = true;
            }
        });
    }
    updatePanelHeader() {
        let header = Resources.getResource('panel.terceraChart') + ' ';
        const chart = this.tChart;
        if (!isNullOrUndefined(chart)) {
            const ins = chart.Instrument();
            if (!isNullOrUndefined(ins))
                header += ins.DisplayName() + ' ';
            const tfi = chart.TimeFrameInfo();
            if (!isNullOrUndefined(tfi)) {
                header +=
                    Resources.getResource('chart.timeframeselector.time') +
                        ' - ' +
                        Periods.ToLocalizedShortPeriod(tfi.Periods);
            }
        }
        void this.set('header', header);
    }
    updateGoToState() {
        var _a;
        const tfi = (_a = this.tChart) === null || _a === void 0 ? void 0 : _a.TimeFrameInfo();
        if (isNullOrUndefined(tfi)) {
            return;
        }
        const isDayPeriod = Periods.GetBasePeriod(tfi.Periods) === Periods.DAY;
        void this.set('isGoToOnlyDate', isDayPeriod);
        const goToDate = this.get('goToDate');
        if (isNullOrUndefined(goToDate)) {
            return;
        }
        if (this.get('isGoToOnlyDate')) {
            goToDate === null || goToDate === void 0 ? void 0 : goToDate.setHours(0, 0, 0, 0);
        }
    }
    localize() {
        super.localize();
        const addOverlayBtn = this.Controls.overlayButton;
        if (!isNullOrUndefined(addOverlayBtn)) {
            const addOverlayText = Resources.getResource('chart.ChartSymbolSelector.OverlayTooltip');
            addOverlayBtn.set('contextMenuItemText', addOverlayText);
            addOverlayBtn.set('tooltip', addOverlayText);
        }
        const timeFrameCB = this.Controls.timeFrameComboBox;
        if (!isNullOrUndefined(timeFrameCB)) {
            timeFrameCB.set('contextMenuItemText', Resources.getResource('chart.agregationType.Button'));
            timeFrameCB.set('tooltip', Resources.getResource('chart.agregationType.Button.ToolTip'));
        }
        const drawingStyleCB = this.Controls.drawingStyleComboBox;
        if (!isNullOrUndefined(drawingStyleCB)) {
            const styleText = Resources.getResource('chart.StyleButton.ToolTip');
            drawingStyleCB.set('contextMenuItemText', styleText);
            drawingStyleCB.set('tooltip', styleText);
        }
        const toolsButton = this.Controls.toolsButton;
        if (!isNullOrUndefined(toolsButton)) {
            toolsButton.set('contextMenuItemText', Resources.getResource('chart.ChartSymbolSelector.drawingButton'));
            toolsButton.set('tooltip', Resources.getResource('chart.ChartSymbolSelector.drawingButton.ToolTip'));
        }
        const indicatorButton = this.Controls.indicatorButton;
        if (!isNullOrUndefined(indicatorButton)) {
            const indicatorText = Resources.getResource('chart.ChartSymbolSelector.addIndicator.ButtonToolTip');
            indicatorButton.set('contextMenuItemText', indicatorText);
            indicatorButton.set('tooltip', indicatorText);
        }
        const objectManagerButton = this.Controls.objectManagerButton;
        if (!isNullOrUndefined(objectManagerButton)) {
            const objectManagerText = Resources.getResource('chart.ChartSymbolSelector.ObjectManagerTooltip');
            objectManagerButton.set('contextMenuItemText', objectManagerText);
            objectManagerButton.set('tooltip', objectManagerText);
        }
        const templatesEditable = this.Controls.editableList;
        if (!isNullOrUndefined(templatesEditable)) {
            const templatesText = Resources.getResource('chart.templates.tooltip');
            templatesEditable.set('contextMenuItemText', templatesText);
            templatesEditable.set('tooltip', templatesText);
        }
        const mouseTradeButton = this.Controls.mouseTradeButton;
        if (!isNullOrUndefined(mouseTradeButton)) {
            const mouseTradingButtonText = Resources.getResource('chart.ChartSymbolSelector.mouseTradeButton.ToolTip');
            mouseTradeButton.set('contextMenuItemText', mouseTradingButtonText);
            mouseTradeButton.set('tooltip', mouseTradingButtonText);
        }
        const oeButton = this.Controls.orderEntry;
        if (!isNullOrUndefined(oeButton)) {
            const oeButtonText = Resources.getResource('chart.ChartSymbolSelector.oeButton.ToolTip');
            oeButton.set('contextMenuItemText', oeButtonText);
            oeButton.set('tooltip', oeButtonText);
        }
        this.updateTimeFrameComboBox();
        this.updateDrawingStyleComboBox();
        if (!isNullOrUndefined(this.terceraChartRactive)) {
            this.terceraChartRactive.localize();
        }
        if (!isNullOrUndefined(this.chartOE)) {
            this.chartOE.localize();
        }
        const toolsPanel = this.Controls.toolsPanel;
        if (!isNullOrUndefined(toolsPanel)) {
            toolsPanel.localize();
        }
        this.localizeGoTo();
    }
    // #region ICaller
    Properties() {
        let properties = this.isApplyStyleProperties ? [] : super.Properties();
        properties = properties.concat(this.terceraChartRactive.Properties());
        let prop = null;
        // Additional
        let SeparatorGroup = '#3#' + Resources.getResource('property.SeparatorGroup.Additional');
        const terceraChart = this.terceraChartRactive.terceraChart;
        const mainToolBarAction = TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.MainToolBar);
        if (terceraChart.TerceraChartActionProcessor.GetTerceraChartActionState(mainToolBarAction).Visible) {
            prop = new DynProperty('panel.caption.showHideToolbar', this.get('showMainToolBarPanel'), DynProperty.BOOLEAN, DynProperty.VISUAL_GROUP);
            prop.separatorGroup = SeparatorGroup;
            prop.sortIndex = 3;
            properties.push(prop);
        }
        prop = new DynProperty('ChartDrawingToolsToolBar', this.get('showToolsPanel'), DynProperty.BOOLEAN, DynProperty.VISUAL_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 0;
        properties.push(prop);
        const analyzeToolBarAction = TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.AnalyseToolBar);
        if (terceraChart.TerceraChartActionProcessor.GetTerceraChartActionState(analyzeToolBarAction).Visible) {
            prop = new DynProperty('ChartAnalyseToolsToolBar', this.get('showAnalyzeToolsPanel'), DynProperty.BOOLEAN, DynProperty.VISUAL_GROUP);
            prop.separatorGroup = SeparatorGroup;
            prop.sortIndex = 1;
            properties.push(prop);
        }
        const dataSourceOpen = this.get('dataSourceOpen');
        properties.push(new DynProperty('dataSourceOpen', dataSourceOpen, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        // OE
        const oePanel = this.chartOE;
        const isShowOE = this.get('isShowOE');
        SeparatorGroup = '#0#' + Resources.getResource('property.VisibilitySeparatorGroup');
        prop = new DynProperty('property.chart.showingOE', isShowOE, DynProperty.BOOLEAN, DynProperty.CHART_OE_GROUP);
        prop.sortIndex = 0;
        prop.separatorGroup = SeparatorGroup;
        prop.assignedProperty = [
            'OE_ShowBuyMarketSellMarket',
            'OE_ShowSellBidBuyASk',
            'OE_AddBidAddAsk'
            // "OE_Small",
            // "OE_ShowSLTP"
        ];
        properties.push(prop);
        SeparatorGroup = '#2#' + Resources.getResource('property.SeparatorViewButtons');
        prop = new DynProperty('OE_ShowBuyMarketSellMarket', oePanel.get('_userPreferredShowBuySellMarket'), DynProperty.BOOLEAN, DynProperty.CHART_OE_GROUP);
        prop.sortIndex = 1;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = isShowOE;
        properties.push(prop);
        const btnGr = DynProperty.CHART_OE_GROUP;
        prop = new DynProperty('OE_ShowSellBidBuyASk', oePanel.get('_userPreferredShowSellBidBuyAsk'), DynProperty.BOOLEAN, btnGr);
        prop.sortIndex = 2;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = isShowOE;
        properties.push(prop);
        prop = new DynProperty('OE_AddBidAddAsk', oePanel.get('_userPreferredShowAddBidAsk'), DynProperty.BOOLEAN, btnGr);
        prop.sortIndex = 3;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = isShowOE;
        properties.push(prop);
        // Hidden
        const ins = this.get('instrumentItem');
        if (!isNullOrUndefined(ins)) {
            properties.push(new DynProperty('symbol', ins.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        const acc = this.get('accountItem');
        if (!isNullOrUndefined(acc)) {
            properties.push(new DynProperty('account', acc.AcctNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        if (!isNullOrUndefined(this.Controls.editableList)) {
            const tmpItem = this.Controls.editableList.get('selectedItem');
            const tmp = !isNullOrUndefined(tmpItem) ? tmpItem.text : null;
            if (!isNullOrUndefined(tmp)) {
                properties.push(new DynProperty('selectedTemplate', tmp, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
            }
        }
        const goToDate = this.get('goToDate');
        if (!isNullOrUndefined(goToDate)) {
            properties.push(new DynProperty('goToDate', goToDate, DynProperty.DATE, DynProperty.HIDDEN_GROUP));
        }
        properties.push(new DynProperty('isOpenObjectManagerScreen', !isNullOrUndefined(this.objectManagerScreen), DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('', '', DynProperty.SEPARATOR, DynProperty.SEPARATOR_GROUP1));
        properties.push(new DynProperty('', '', DynProperty.SEPARATOR, DynProperty.SEPARATOR_GROUP2));
        return properties;
    }
    applyStyleProperties() {
        this.isApplyStyleProperties = true;
        super.applyStyleProperties();
        this.isApplyStyleProperties = false;
    }
    callBack(properties) {
        const isApplyTemplate = this.isApplyStyleProperties;
        if (!this.isApplyStyleProperties) {
            super.callBack(properties);
        }
        let dp = null;
        if (!isApplyTemplate) {
            // Hidden
            void this.set('instrumentItem', this.getCallBackInstrument(properties, 'symbol'));
            dp = DynProperty.getPropertyByName(properties, 'account');
            if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value) && !LinkedSystem.accLinkingActive) {
                // #119833
                void this.set('accountItem', SessionSettings.getDefValueFromObj(DataCache.Accounts[dp.value], DataCache.Accounts));
            }
        }
        dp = DynProperty.getPropertyByName(properties, 'panel.caption.showHideToolbar');
        if (!isNullOrUndefined(dp)) {
            void this.set('showMainToolBarPanel', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'ChartDrawingToolsToolBar');
        if (!isNullOrUndefined(dp)) {
            void this.set('showToolsPanel', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'ChartAnalyseToolsToolBar');
        if (!isNullOrUndefined(dp)) {
            void this.set('showAnalyzeToolsPanel', dp.value);
        }
        const oePanel = this.chartOE;
        // У когото может быть открыто ОЕ до его скрытия, нужно его принудительно закрыть
        const hideOE = Resources.isHidden('chart.ChartSymbolSelector.oeButton');
        if (hideOE) {
            void this.set('isShowOE', false);
        }
        else {
            dp = DynProperty.getPropertyByName(properties, 'property.chart.showingOE');
            if (!isNullOrUndefined(dp)) {
                void this.set('isShowOE', dp.value);
            }
        }
        // dp = DynProperty.getPropertyByName(properties, "OE_Small");
        // if (dp) this.set('oeSmall', dp.value);
        // dp = DynProperty.getPropertyByName(properties, "OE_ShowSLTP");
        // if (dp) oePanel.OE_ShowSLTP = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'OE_ShowBuyMarketSellMarket');
        if (!isNullOrUndefined(dp)) {
            oePanel.set('_userPreferredShowBuySellMarket', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'OE_ShowSellBidBuyASk');
        if (!isNullOrUndefined(dp)) {
            oePanel.set('_userPreferredShowSellBidBuyAsk', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'OE_AddBidAddAsk');
        if (!isNullOrUndefined(dp)) {
            oePanel.set('_userPreferredShowAddBidAsk', dp.value);
        }
        this.terceraChartRactive.callBack(properties);
        if (!isApplyTemplate) {
            dp = DynProperty.getPropertyByName(properties, 'selectedTemplate');
            const cb = this.Controls.editableList;
            if (isValidString(dp === null || dp === void 0 ? void 0 : dp.value) && !isNullOrUndefined(cb)) {
                cb.restoreCorrectSelectItem(dp.value);
                this.initialTemplateSelected = true;
            }
        }
        dp = DynProperty.getPropertyByName(properties, 'dataSourceOpen');
        if (!isNullOrUndefined(dp)) {
            void this.set('dataSourceOpen', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'goToDate');
        if (!isNullOrUndefined(dp)) {
            const goToDate = new Date(dp.value);
            void this.set({ goToDate });
            this.updateGoToState();
        }
        dp = DynProperty.getPropertyByName(properties, 'isOpenObjectManagerScreen');
        if (dp === null || dp === void 0 ? void 0 : dp.value) {
            this.addObjectManagerScreen();
        }
    }
    // #endregion
    callCallback(properties) {
        this.callBack(properties);
    }
    themeChange() {
        const Controls = this.Controls;
        if (!isNullOrUndefined(this.terceraChartRactive)) {
            this.terceraChartRactive.themeChange();
        }
        const toolsPanel = Controls.toolsPanel;
        if (!isNullOrUndefined(toolsPanel)) {
            toolsPanel.themeChange();
        }
    }
    onTimeFrameComboBox_ComboItemClicked(context, newVal) {
        const model = this.terceraChartRactive.terceraChart.model;
        const oldTFInfo = model.GetTimeFrameInfo();
        const controller = this.terceraChartRactive.terceraChart.chartController;
        controller.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.TFI), oldTFInfo.Copy({ period: newVal.tag }));
    }
    onDrawingStyleComboBox_ComboItemClicked(context, newVal) {
        this.terceraChartRactive.terceraChart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Style), newVal.tag);
    }
    // public onLoadRangeComboBox_ComboItemClicked (context, newVal): void {
    //     this.terceraChartRactive.terceraChart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Range), newVal.tag);
    // }
    updateTimeFrameComboBox() {
        var _a;
        if (isNullOrUndefined((_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart)) {
            return;
        }
        const cBox = this.Controls.timeFrameComboBox;
        const items = [
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.TIC),
                value: Periods.TIC
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.MIN),
                value: Periods.MIN
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.MIN5),
                value: Periods.MIN5
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.MIN15),
                value: Periods.MIN15
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.MIN30),
                value: Periods.MIN30
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.HOUR),
                value: Periods.HOUR
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.HOUR4),
                value: Periods.HOUR4
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.DAY),
                value: Periods.DAY
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.WEEK),
                value: Periods.WEEK
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.MONTH),
                value: Periods.MONTH
            },
            {
                type: NewContextMenuItemType.Default,
                text: Periods.ToLocalizedShortPeriod(Periods.YEAR),
                value: Periods.YEAR
            }
        ];
        if (Resources.isHidden('chart.AllowedPeriods.1T')) {
            // || DataCache.isAllowed(IsAllowed.RequestsTypes.isAllowed_PERIOD_TICK) )
            items.splice(0, 1);
        }
        if (!isNullOrUndefined(cBox)) {
            cBox.set({ listWidth: 84, items });
        }
        const tf = this.terceraChartRactive.terceraChart.model.GetTimeFrameInfo();
        if (!isNullOrUndefined(tf) && !isNullOrUndefined(cBox)) {
            cBox.setItembyValue(tf.Periods);
        }
    }
    updateDrawingStyleComboBox() {
        var _a;
        if (isNullOrUndefined((_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart)) {
            return;
        }
        const chart = this.terceraChartRactive.terceraChart;
        const allowedStyles = chart.model.CurrentAllowedStyles;
        const len = allowedStyles.length;
        const items = [];
        for (let i = 0; i < len; i++) {
            const style = allowedStyles[i];
            items.push({
                type: NewContextMenuItemType.Default,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(style),
                text: TerceraChartUtils.ToLocalizedTerceraChartDrawingType(style),
                value: style
            });
        }
        const cBox = this.Controls.drawingStyleComboBox;
        if (!isNullOrUndefined(cBox)) {
            cBox.set({ items, enabled: len > 1 });
            cBox.setItembyValue(chart.model.GetChartDrawingType());
            void this.set('drawingStyleIcon', TerceraChartUtils.getChartDrawingIconByType(chart.model.GetChartDrawingType()));
        }
    }
    // public updateLoadRangeComboBox (): void {
    //     if (isNullOrUndefined(this.terceraChartRactive?.terceraChart)) {
    //         return;
    //     }
    //     const model = this.terceraChartRactive.terceraChart.model;
    //     const periods = model.CurrentAllowedPeriods;
    //     const len = periods.length;
    //     const cBoxItems = [];
    //     let selItem = null;
    //     const curPeriodDesc = model.GetPeriodDesc();
    //     for (let i = 0; i < len; i++) {
    //         const p = periods[i];
    //         cBoxItems.push({ text: p.toString(), value: p });
    //         if (curPeriodDesc.equals(p)) {
    //             selItem = p;
    //         }
    //     }
    //     const cBox = this.Controls.loadRangeComboBox;
    //     if (!isNullOrUndefined(cBox)) {
    //         cBox.set('items', cBoxItems);
    //         cBox.setItembyValue(selItem);
    //     }
    // }
    initChart() {
        this.terceraChartRactive = this.Controls.chart;
        if (isNullOrUndefined(this.terceraChartRactive)) {
            return;
        }
        this.terceraChartRactive.terceraChart.OnLoadingStart.Subscribe(this.onLoadingStart, this);
        this.terceraChartRactive.terceraChart.OnLoadingFinish.Subscribe(this.onLoadingFinish, this);
        const model = this.terceraChartRactive.terceraChart.model;
        model.ModelItemChanged.Subscribe(this.MVCModelItemChanged, this);
        const controller = this.terceraChartRactive.terceraChart.chartController;
        controller.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Instrument), this.get('instrumentItem'));
        controller.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Account), this.get('accountItem'));
    }
    MVCModelItemChanged(item) {
        switch (item.ItemType) {
            case ModelDataType.Style:
                this.updateDrawingStyleComboBox();
                break;
            case ModelDataType.Instrument:
                this.updatePanelHeader();
                break;
            case ModelDataType.TFI:
                this.updateTimeFrameComboBox();
                this.updatePanelHeader();
                this.updateGoToState();
                break;
            case ModelDataType.Range:
                // this.updateLoadRangeComboBox();
                break;
        }
    }
    GetTerceraChart() {
        var _a;
        if (!isNullOrUndefined(this._terceraChart)) {
            return this._terceraChart;
        }
        if (!isNullOrUndefined((_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart)) {
            this._terceraChart = this.terceraChartRactive.terceraChart;
        }
        return this._terceraChart;
    }
    TickAsync() {
        const chart = this.GetTerceraChart();
        if (chart === null || chart === void 0 ? void 0 : chart.needRedraw) {
            chart.needRedraw = false;
            chart.Draw();
            chart.AddForceUpdate();
        }
    }
    overlay_btnClick() {
        void this.set('isOverlayScreenOpen', true);
        TerceraAddOverlayScreen.addNewOverlay(this.terceraChartRactive.terceraChart, this.overlayScreenClosed);
    }
    onIndicatorBtnClick() {
        void this.set('isIndicatorLookupOpen', true);
        const params = new LookupDropDownShowParams();
        params.callBack = this.indicatorLookupCB;
        params.closeCallback = this.indicatorLookupClosed;
        TerceraIndicatorLookupDropDownForm.ShowForm(params);
    }
    addObjectManagerScreen(typePanel = null) {
        if (isNullOrUndefined(this.objectManagerScreen)) {
            const chart = this.terceraChartRactive.terceraChart;
            const screen = MainWindowManager.Factory.addScreen(ScreensNames.ObjectManagerScreen);
            screen.OnClose.Subscribe(this.onCloseObjectManagerScreen, this);
            screen.setChartToScreen(chart);
            this.objectManagerScreen = screen;
            if (!isNullOrUndefined(typePanel)) {
                const panels = screen.findAllComponents().filter((component) => component instanceof TerceraWindowBase);
                for (const panel of panels) {
                    if (panel.getType() === typePanel) {
                        void screen.set('forcedSelectedPanel', panel);
                        break;
                    }
                }
            }
        }
        const objectManagerButton = this.Controls.objectManagerButton;
        if (!isNullOrUndefined(objectManagerButton)) {
            objectManagerButton.set('checked', true);
        }
    }
    onCloseObjectManagerScreen() {
        this.objectManagerScreen.OnClose.UnSubscribe(this.onCloseObjectManagerScreen, this);
        this.objectManagerScreen = null;
        const objectManagerButton = this.Controls.objectManagerButton;
        if (!isNullOrUndefined(objectManagerButton)) {
            objectManagerButton.set('checked', false);
        }
    }
    indicatorLookupCB(selectedItem) {
        if (typeof selectedItem === 'object' && isValidString(selectedItem.FullName)) {
            this.terceraChartRactive.terceraChart.AddIndicator(IndicatorManager.GetIndicator(selectedItem.FullName));
        }
    }
    // #region ITerceraChartPanelContext
    ShowScreen(type, parameters) {
        switch (type) {
            case ITerceraChartShowScreenType.ChartSettings: {
                if (this.AllowSettings) {
                    const headerString = Resources.getResource('panel.terceraChart') +
                        ' ' +
                        Resources.getResource('PanelSettingsScreen.TextSmall');
                    if (parameters === null || parameters === void 0 ? void 0 : parameters.PanelSettingsDefaultPage) {
                        PanelSettingsScreen.EditProperties(this, parameters.PanelSettingsDefaultPage, headerString);
                    }
                    else {
                        PanelSettingsScreen.EditProperties(this, DynProperty.DATA_STYLE_GROUP, headerString);
                    }
                }
                break;
            }
            case ITerceraChartShowScreenType.Properties: {
                const caller = parameters.caller;
                const toolStateInfoDict = TerceraChartActionProcessor.ToolStateInfoDict;
                const toolType = caller.dataCacheTool.ToolType;
                const headerString = Resources.getResource(toolStateInfoDict[toolType].locKey) +
                    ' ' +
                    Resources.getResource('screen.properties.common');
                PanelSettingsScreen.EditProperties(caller, null, headerString, this);
                return null;
            }
            case ITerceraChartShowScreenType.UIManipulation: {
                const isSet = parameters.isSet;
                const toolBarType = parameters.toolBarType;
                switch (toolBarType) {
                    case TerceraChartToolbarsEnum.MainToolBar:
                        if (isSet) {
                            void this.toggle('showMainToolBarPanel');
                        }
                        return this.get('showMainToolBarPanel');
                    case TerceraChartToolbarsEnum.DrawingToolBar:
                        if (isSet) {
                            void this.toggle('showToolsPanel');
                        }
                        return this.get('showToolsPanel');
                    case TerceraChartToolbarsEnum.AnalyseToolBar:
                        if (isSet) {
                            void this.toggle('showAnalyzeToolsPanel');
                        }
                        return this.get('showAnalyzeToolsPanel');
                    case TerceraChartToolbarsEnum.OrderEntry:
                        if (isSet) {
                            void this.toggle('isShowOE');
                        }
                        return this.get('isShowOE');
                    case TerceraChartToolbarsEnum.AllPricePanel:
                        return {};
                    /* TODO.
                if (isSet)
                {
                    allpricePanelSettings.Visible = !allpricePanelSettings.Visible;
                    UpdateChildPanelsState();
                }
                return new object[] { allpricePanelSettings.Visible, allpricePanelSettings.Visible && allPricePanel != null ? allPricePanel.quickTable.RealRowsCount > 0 : false };
                */
                    case TerceraChartToolbarsEnum.TimeAndSalesPanel:
                        return {};
                    /* TODO.
                if (isSet)
                {
                    tsPanelSettings.Visible = !tsPanelSettings.Visible;
                    UpdateChildPanelsState();
                }
                return new object[] { tsPanelSettings.Visible, tsPanelSettings.Visible && tsPanel != null ? tsPanel.quickTable.RealRowsCount > 0 : false };
                */
                }
                break;
            }
            case ITerceraChartShowScreenType.OverlayScreen:
                TerceraAddOverlayScreen.addNewOverlay(this.terceraChartRactive.terceraChart);
                return null;
            case ITerceraChartShowScreenType.ScriptLookup:
                this.onIndicatorBtnClick();
                return null;
            case ITerceraChartShowScreenType.NewPanel: {
                this.openNewPanel(parameters.panelName);
                return null;
            }
            case ITerceraChartShowScreenType.ChartTrading:
                if (parameters === null || parameters === void 0 ? void 0 : parameters.hasOwnProperty('tradingFlag')) {
                    this.MouseTradingEnabledSet(parameters.tradingFlag);
                }
                else {
                    this.MouseTradingEnabledSet(!this.MouseTradingEnabledGet());
                }
                break;
        }
        return null;
    }
    // #endregion
    MouseTradingEnabledGet() {
        const chart = this.terceraChartRactive.terceraChart;
        return chart.TerceraChartTradingToolsRenderer.MouseTradeEnabled && !TradingLockUtils.TradingLock.tradingLocked;
    }
    MouseTradingEnabledSet(value) {
        const chart = this.terceraChartRactive.terceraChart;
        chart.TerceraChartTradingToolsRenderer.MouseTradeEnabled = value;
        this.activateCtrlMouseTrading(value);
        this.UpdateMouseTradingButton();
    }
    CtrlMouseTradingEnabledGet() {
        const chart = this.terceraChartRactive.terceraChart;
        return (chart.TerceraChartTradingToolsRenderer.CtrlMouseTradeEnabled && !TradingLockUtils.TradingLock.tradingLocked);
    }
    CtrlMouseTradingEnabledSet(value) {
        const chart = this.terceraChartRactive.terceraChart;
        chart.TerceraChartTradingToolsRenderer.CtrlMouseTradeEnabled = value;
        this.UpdateMouseTradingButton();
    }
    UpdateMouseTradingButton() {
        const mouseTradeButton = this.Controls.mouseTradeButton;
        if (isNullOrUndefined(mouseTradeButton))
            return;
        const checked = this.MouseTradingEnabledGet() || this.CtrlMouseTradingEnabledGet();
        mouseTradeButton.set('checked', checked);
        this.terceraChartRactive.terceraChart.UpdateCursor();
        if (isNullOrUndefined(this.myVisualWidget)) {
            return;
        }
        void this.myVisualWidget.show(checked).then(() => {
            if (!checked) {
                return;
            }
            const mL = this.myVisualWidget.get('left');
            const mVWidthC = this.myVisualWidget.get('width');
            const mVWidth = this.terceraChartRactive.get('width');
            // TODO сделать по нормальному наверное
            if (!this.myVisualWidget.Placed ||
                mL < 0 ||
                mL + mVWidthC > mVWidth + 10 /* небольшое окно на полгрешность справа */) {
                void this.myVisualWidget.set({ left: mVWidth / 2 - mVWidthC / 2, top: 0 });
                this.myVisualWidget.Placed = true;
            }
        });
    }
    mouseTradeButton_btnClick() {
        this.terceraChartRactive.terceraChart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ChartTrading));
    }
    get tChart() {
        var _a;
        return (_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart;
    }
    onGoToLoadingStart(goToDate) {
        if (this.get('isGoToOnlyDate')) {
            goToDate === null || goToDate === void 0 ? void 0 : goToDate.setHours(0, 0, 0, 0);
        }
        void this.set({ goToDate });
        if (isNullOrUndefined(goToDate) || !this.tChart.HasCorrectData()) {
            return;
        }
        this.tChart.goToAction(goToDate);
    }
    ChartVisualTrading(order, newData) {
        return this.ChartTradingCore.ChartVisualTrading(order, newData, this.get('instrumentItem'));
    }
    IsAllowedAction(action) {
        return this.ChartTradingCore.IsAllowedAction(action);
    }
    // TODO. Very Ugly.
    changeQuantity(order, newData) {
        const quant = newData.quantity;
        const modifyOrdObj = this.createModifyOrderObject(order);
        modifyOrdObj.setTradingData({ quantity: quant });
        DataCache.FOrderExecutor.modifyOrderPromiseWithCallbacks(modifyOrdObj)
            .then(function (confirmed) {
            if (!isNullOrUndefined(newData.CancelCallback) && !confirmed) {
                newData.CancelCallback();
            }
        })
            .catch(function () {
            const ex = new CustomErrorClass('ChangeQuantity error', 'ChartPanel.changeQuantity', 'changeQuantity -> modifyOrderPromiseWithCallbacks');
            ErrorInformationStorage.GetException(ex);
            if (!isNullOrUndefined(newData.CancelCallback)) {
                newData.CancelCallback();
            }
        })
            .finally(function () {
            // TODO. Refactor.
            modifyOrdObj.dispose();
        });
    }
    // TODO. Ugly.
    createModifyOrderObject(order) {
        const orderTypeObj = DataCache.OrderParameterContainer.GetOrderType(order.OrderType);
        return orderTypeObj.createModifyOrderObject({
            dataCache: DataCache,
            order
        });
    }
    updateSettings() {
        var _a;
        super.updateSettings();
        (_a = this.chartOE) === null || _a === void 0 ? void 0 : _a.updateTradingAllowedStuff();
    }
    onGlobalKeyDown(btn, modificators) {
        var _a;
        if (!this.isPanelOrChartFocused()) {
            return;
        }
        if (((_a = this.myTradeNumeric) === null || _a === void 0 ? void 0 : _a.get('visible')) && btn === KeyCode.ENTER) {
            this.onEnterPressed();
        }
        // Removed due to id:123429
        // this.activateCtrlMouseTrading(
        //     btn === KeyCode.CTRL &&
        //     !modificators.ALT &&
        //     !modificators.SHIFT);
    }
    onGlobalKeyUp(btn, modificators) {
        if (!this.isPanelOrChartFocused()) {
            const chart = this.terceraChartRactive.terceraChart;
            const ttRenderer = chart.TerceraChartTradingToolsRenderer;
            if (!ttRenderer.CtrlMouseTradeEnabled) {
                return;
            }
        }
        this.activateCtrlMouseTrading(false);
    }
    isPanelOrChartFocused() {
        return this.get('focused') || this.terceraChartRactive.get('focused');
    }
    // TODO.
    lostFocus() {
        super.lostFocus();
        this.activateCtrlMouseTrading(false);
    }
    activateCtrlMouseTrading(activate) {
        if (isNullOrUndefined(this.terceraChartRactive)) {
            return;
        }
        // http://tp.pfsoft.net/entity/56578
        if (this.MouseTradingEnabledGet()) {
            return;
        }
        const hideOE = Resources.isHidden('chart.ChartSymbolSelector.oeButton');
        if (hideOE) {
            return;
        }
        const chart = this.terceraChartRactive.terceraChart;
        const ttRenderer = chart.TerceraChartTradingToolsRenderer;
        // http://tp.pfsoft.net/entity/56570
        if (ttRenderer.CtrlMouseTradeEnabled === activate) {
            return;
        }
        ttRenderer.CtrlMouseTradeEnabled = activate;
        this.UpdateMouseTradingButton();
        chart.IsDirty(LayersEnum.Tools);
    }
    dispose() {
        KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
        KeyEventProcessor.OnKeyUp.UnSubscribe(this.onGlobalKeyUp, this);
        DataCache.OnHistoryChangedAndNeedReload.UnSubscribe(this.checkNeedReload, this);
        if (!isNullOrUndefined(this.Controls.editableList)) {
            this.Controls.editableList.OnItemSave.UnSubscribe(this.onSaveTemplate, this);
            this.Controls.editableList.OnSelectList.UnSubscribe(this.onSelectTemplate, this);
        }
        // this.terceraChartRactive.terceraChart.autoLoadingStateChanged.UnSubscribe(this.autoLoadingStateChanged, this);
        // SessionSettings.timeZoneChanged.UnSubscribe(this.terceraChartRactive.terceraChart.RefreshChart, this.terceraChartRactive.terceraChart);
        this.terceraChartRactive.terceraChart.Dispose();
        this.terceraChartRactive.terceraChart.OnLoadingStart.UnSubscribe(this.onLoadingStart, this);
        this.terceraChartRactive.terceraChart.OnLoadingFinish.UnSubscribe(this.onLoadingFinish, this);
        if (!isNullOrUndefined(this.myVisualWidget)) {
            this.myVisualWidget.dispose();
        }
        if (!isNullOrUndefined(this.myTradeNumeric)) {
            this.myTradeNumeric.dispose();
        }
        if (!isNullOrUndefined(this.objectManagerScreen)) {
            this.objectManagerScreen.dispose();
        }
        this.OnResize.UnSubscribe(this.layoutTable, this);
        TradingLockUtils.TradingLock.TradingLockChanged.UnSubscribe(this.updateTradingLockState, this);
        this.terceraChartRactive.terceraChart.TerceraChartActionProcessor.OnActionProcessor.UnSubscribe(this.OnActionProcessor, this);
        super.dispose();
    }
    accountLink_In(accId, fromLinkedSystem = false) {
        if (fromLinkedSystem && !this.OnAccountLinking()) {
            return;
        }
        if (!isNullOrUndefined(DataCache.Accounts[accId])) {
            void this.set('accountItem', DataCache.Accounts[accId]);
        }
        this.localizeAccountLinkTooltip();
        return true;
    }
    symbolLink_In(symbolName) {
        const currentInstrument = this.get('instrumentItem');
        const newInstr = DataCache.getInstrumentByName(symbolName);
        if (!isNullOrUndefined(newInstr)) {
            // убираем не нужный апдейт
            if (!isNullOrUndefined(currentInstrument) && currentInstrument.GetInteriorID() === symbolName) {
                return;
            }
            void this.set('instrumentItem', newInstr);
        }
    }
    symbolLink_Out(newSubscriber, instrument) {
        if (isNullOrUndefined(instrument)) {
            const ins = this.get('instrumentItem');
            if (isNullOrUndefined(ins))
                return;
            instrument = ins;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    getInstrument() {
        return this.get('instrumentItem');
    }
    onSaveTemplate(item) {
        var _a;
        const editableCB = this.Controls.editableList;
        const selectedList = (_a = editableCB.valueOfLists) === null || _a === void 0 ? void 0 : _a.find(({ name }) => name === item.value);
        if (!selectedList)
            return;
        if (!editableCB.get('selectedItem')) {
            editableCB.set('selectedItem', item);
        }
        this.isApplyStyleProperties = true;
        try {
            selectedList.itemsStr = JSON.stringify(this.serialize());
        }
        finally {
            this.isApplyStyleProperties = false;
        }
        editableCB.updateListsValue(null, false, null, true);
    }
    onSelectTemplate(propJSON) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isValidString(propJSON)) {
                return;
            }
            this.isApplyStyleProperties = true;
            this.terceraChartRactive.terceraChart.model.skipRefreshChartInCallback = true;
            const chartTemplate = JSON.parse(propJSON);
            if (!isNullOrUndefined(chartTemplate.xmlSettings)) {
                this.deserializeOldScheme({
                    properties: chartTemplate.properties,
                    xmlSettingsTemplate: chartTemplate.xmlSettings
                });
            }
            else {
                this.deserialize(chartTemplate);
            }
            this.isApplyStyleProperties = false;
            this.terceraChartRactive.terceraChart.model.skipRefreshChartInCallback = false;
            this.terceraChartRactive.terceraChart.RefreshChart();
        });
    }
    selectInitialTemplate() {
        if (this.initialTemplateSelected)
            return;
        const editableList = this.Controls.editableList;
        const selItem = editableList.get('selectedItem');
        if (isNullOrUndefined(selItem))
            return;
        if (editableList.menuItems.includes(selItem)) {
            selItem.selected = false;
            editableList.set('selectedItem', null);
        }
    }
    // setcursor settings to div
    applyCursor(cursor) {
        if (!isNullOrUndefined(this.terceraChartRactive)) {
            void this.terceraChartRactive.setCursor(cursor);
        }
    }
    RefreshInstrumentDescriptions(insArray) {
        var _a;
        super.RefreshInstrumentDescriptions();
        if (!insArray.includes(this.get('instrumentItem'))) {
            return;
        }
        const tCh = (_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart;
        if (isNullOrUndefined(tCh)) {
            return;
        }
        tCh.RefreshChart();
    }
    onHasCorrectDataChanged(hasCorrectData) {
        this.set({
            dataSourceBottom: hasCorrectData ? 42 : 5
        });
    }
    getPanelSize() {
        const { width, height } = this.getCorrectChartPanelSize();
        return { width, height, marginTop: 0, marginBottom: 0 };
    }
    getCorrectChartPanelSize() {
        if (FullScreenManager.IsFullScreenMode) {
            const control = this.getControl();
            return { width: control.offsetWidth, height: control.offsetHeight };
        }
        let mainWindowW = this.get('width');
        if (!isValidNumber(mainWindowW) || mainWindowW === 0) {
            mainWindowW = this.el.offsetWidth;
        }
        let mainWindowH = this.get('height');
        if (!isValidNumber(mainWindowH) || mainWindowH === 0) {
            mainWindowH = this.el.offsetHeight;
        }
        return { width: mainWindowW, height: mainWindowH };
    }
    HotkeyPressed(hotkey) {
        const isPressed = super.HotkeyPressed(hotkey);
        if (isPressed) {
            return true;
        }
        const chart = this.terceraChartRactive.terceraChart;
        if (isNullOrUndefined(chart)) {
            return false;
        }
        const account = this.get('accountItem');
        const instrument = this.get('instrumentItem');
        const hotkeyAction = HotkeysManager.GetActionByHotkeyForPanel(hotkey, this.getType());
        switch (hotkeyAction) {
            case ChartHotkeysEnum.DisplayInFullScreenMode:
                this.toggleFullscreenMode();
                return true;
            case ChartHotkeysEnum.ChangeChartStyle:
                this.forcedChangeDrawingStyleComboBox();
                return true;
            case ChartHotkeysEnum.ZoomIn:
                chart.ZoomIn();
                return true;
            case ChartHotkeysEnum.ZoomOut:
                chart.ZoomOut();
                return true;
            case ChartHotkeysEnum.AutoManualScaling:
                chart.AutoManualScaling();
                return true;
            case ChartHotkeysEnum.MouseTrading:
                chart.MouseTrading();
                return true;
            case ChartHotkeysEnum.ShowHideInfoWindow:
                chart.ShowHideInfoWindow();
                return true;
            case ChartHotkeysEnum.AddIndicator:
                this.onIndicatorBtnClick();
                return true;
            case ChartHotkeysEnum.RemoveDrawings:
                chart.RemoveDrawings();
                return true;
            case ChartHotkeysEnum.ShowFilledOrders:
                chart.ShowFilledOrders();
                return true;
            case ChartHotkeysEnum.SetPeriod_Tick:
                this.forcedChangeTimeFrameComboBox(Periods.TIC);
                return true;
            case ChartHotkeysEnum.SetPeriod_1m:
                this.forcedChangeTimeFrameComboBox(Periods.MIN);
                return true;
            case ChartHotkeysEnum.SetPeriod_5m:
                this.forcedChangeTimeFrameComboBox(Periods.MIN5);
                return true;
            case ChartHotkeysEnum.SetPeriod_15m:
                this.forcedChangeTimeFrameComboBox(Periods.MIN15);
                return true;
            case ChartHotkeysEnum.SetPeriod_30m:
                this.forcedChangeTimeFrameComboBox(Periods.MIN30);
                return true;
            case ChartHotkeysEnum.SetPeriod_1H:
                this.forcedChangeTimeFrameComboBox(Periods.HOUR);
                return true;
            case ChartHotkeysEnum.SetPeriod_4H:
                this.forcedChangeTimeFrameComboBox(Periods.HOUR4);
                return true;
            case ChartHotkeysEnum.SetPeriod_1D:
                this.forcedChangeTimeFrameComboBox(Periods.DAY);
                return true;
            case ChartHotkeysEnum.SetPeriod_1W:
                this.forcedChangeTimeFrameComboBox(Periods.WEEK);
                return true;
            case ChartHotkeysEnum.SetPeriod_1M:
                this.forcedChangeTimeFrameComboBox(Periods.MONTH);
                return true;
            case ChartHotkeysEnum.SetPeriod_1Y:
                this.forcedChangeTimeFrameComboBox(Periods.YEAR);
                return true;
            case ChartHotkeysEnum.ObjectManagerIndicators:
                this.addObjectManagerScreen(PanelNames.IndicatorsManagerPanel);
                return true;
            case ChartHotkeysEnum.ObjectManagerDrawings:
                this.addObjectManagerScreen(PanelNames.DrawingsManagerPanel);
                return true;
            case ChartHotkeysEnum.ObjectManagerOverlays:
                this.addObjectManagerScreen(PanelNames.OverlaysManagerPanel);
                return true;
            case ChartHotkeysEnum.DrawLine:
                chart.HotkeyDrawingProcess(DataCacheToolType.Line);
                return true;
            case ChartHotkeysEnum.DrawVerticalLine:
                chart.HotkeyDrawingProcess(DataCacheToolType.VerticalLine);
                return true;
            case ChartHotkeysEnum.DrawHorizontalLine:
                chart.HotkeyDrawingProcess(DataCacheToolType.HorizontalLine);
                return true;
            case ChartHotkeysEnum.DrawArrow:
                chart.HotkeyDrawingProcess(DataCacheToolType.ArrowLabel);
                return true;
            case ChartHotkeysEnum.DrawComments:
                chart.HotkeyDrawingProcess(DataCacheToolType.CommentsLabel);
                return true;
            case ChartHotkeysEnum.DrawFibonacciRetracement:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciRetracement);
                return true;
            case ChartHotkeysEnum.DrawFibonacciExpansion:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciExpansion);
                return true;
            case ChartHotkeysEnum.DrawFibonacciArc:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciArc);
                return true;
            case ChartHotkeysEnum.DrawFibonacciTimeGoalAnalysis:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciTimeGoal);
                return true;
            case ChartHotkeysEnum.DrawFibonacciFans:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciFans);
                return true;
            case ChartHotkeysEnum.DrawFibonacciSpiral:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciSpiral);
                return true;
            case ChartHotkeysEnum.DrawFibonacciTimeZone:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciTimeZone);
                return true;
            case ChartHotkeysEnum.DrawFibonacciPhiChannel:
                chart.HotkeyDrawingProcess(DataCacheToolType.FibonacciPhiChannel);
                return true;
            case ChartHotkeysEnum.DrawTriangle:
                chart.HotkeyDrawingProcess(DataCacheToolType.Triangle);
                return true;
            case ChartHotkeysEnum.DrawPolygon:
                chart.HotkeyDrawingProcess(DataCacheToolType.Polygon);
                return true;
            case ChartHotkeysEnum.DrawRectangle:
                chart.HotkeyDrawingProcess(DataCacheToolType.Rectangle);
                return true;
            case ChartHotkeysEnum.ShowHideGrid:
                chart.ShowHideGrid();
                return true;
            case ChartHotkeysEnum.ShowOpenPositions:
                chart.ShowOpenPositions();
                return true;
            case ChartHotkeysEnum.ShowVolume:
                chart.ShowVolume();
                return true;
            case ChartHotkeysEnum.ShowWorkingOrders:
                chart.ShowWorkingOrders();
                return true;
            case ChartHotkeysEnum.CancelLastOrderOnSelectedInstrument:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.LastOrder, PlacedFrom.WEB_CHART_VISUAL);
            case ChartHotkeysEnum.CancelAllActiveOrdersOnSelectedInstrument:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.AllOrders, PlacedFrom.WEB_CHART_VISUAL);
            case ChartHotkeysEnum.CancelBuyOrderClosestToTheLastPrice:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.BuyOrder, PlacedFrom.WEB_CHART_VISUAL);
            case ChartHotkeysEnum.CancelSellOrderClosestToTheLastPrice:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.SellOrder, PlacedFrom.WEB_CHART_VISUAL);
        }
        return false;
    }
    getDrawingToolsLocation() {
        if (this.get('isChartHasMinSize') || !this.get('showToolsPanel')) {
            return 'drawing-tools-none';
        }
        const isVertical = this.get('verticalToolsPanel');
        const isLeftOrTop = this.get('leftOrTopToolsPanel');
        if (isVertical && isLeftOrTop) {
            return 'drawing-tools-left';
        }
        else if (!isVertical && isLeftOrTop) {
            return 'drawing-tools-top';
        }
        else if (isVertical && !isLeftOrTop) {
            return 'drawing-tools-right';
        }
        else {
            return 'drawing-tools-down';
        }
    }
    closeAddidionalScreens(mainComponent) {
        var _a, _b, _c;
        const controls = mainComponent.Controls;
        (_a = controls.addOverlayScreen) === null || _a === void 0 ? void 0 : _a.close();
        (_b = controls.renameScreen) === null || _b === void 0 ? void 0 : _b.close();
        (_c = this.objectManagerScreen) === null || _c === void 0 ? void 0 : _c.close();
        for (const control of Object.values(controls)) {
            if (control instanceof PropertySetupScreen) {
                control.close();
            }
        }
    }
    forcedChangeTimeFrameComboBox(newVal) {
        const cBox = this.Controls.timeFrameComboBox;
        if (!isNullOrUndefined(cBox)) {
            cBox.forcedChangeMenuItemSelected(newVal);
        }
        const model = this.terceraChartRactive.terceraChart.model;
        const oldTFInfo = model.GetTimeFrameInfo();
        const controller = this.terceraChartRactive.terceraChart.chartController;
        controller.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.TFI), oldTFInfo.Copy({ period: newVal }));
    }
    forcedChangeDrawingStyleComboBox() {
        const chart = this.terceraChartRactive.terceraChart;
        const allowedStyles = chart.model.CurrentAllowedStyles;
        const oldValue = chart.model.GetChartDrawingType();
        const newValue = !isNullOrUndefined(allowedStyles[oldValue + 1])
            ? allowedStyles[oldValue + 1]
            : allowedStyles[0];
        const cBox = this.Controls.drawingStyleComboBox;
        if (!isNullOrUndefined(cBox)) {
            cBox.forcedChangeMenuItemSelected(newValue);
        }
        const controller = chart.chartController;
        controller.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Style), newValue);
    }
    localizeGoTo() {
        const goToText = Resources.getResource('chart.goTo.Button');
        const cancelBtnText = Resources.getResource('button.cancel');
        void this.set({
            goToHeader: goToText,
            goToBtnText: goToText,
            goToButtons: [
                new DateSelectorButton(goToText, this.onGoToLoadingStart.bind(this), 'js-button-blue'),
                new DateSelectorButton(cancelBtnText, () => { }, 'js-button-gray25')
            ]
        });
    }
    onLoadingStart() {
        this.set('chartIsLoading', true);
    }
    onLoadingFinish() {
        const chartSerializedSettings = this.get('chartSerializedSettings');
        if (!isNullOrUndefined(chartSerializedSettings)) {
            void this.set('chartSerializedSettings', null);
            if (!isNullOrUndefined(this.objectManagerScreen)) {
                this.objectManagerScreen.repopulate();
            }
        }
        this.set('chartIsLoading', false);
    }
    OnActionProcessor(action) {
        if (isNullOrUndefined(action)) {
            return;
        }
        switch (action.Type) {
            case TerceraChartActionEnum.ObjectManager:
                this.addObjectManagerScreen();
                break;
        }
    }
    indicatorLookupClosed() {
        void this.set('isIndicatorLookupOpen', false);
    }
    overlayScreenClosed() {
        void this.set('isOverlayScreenOpen', false);
    }
    // #region ISerializable
    serialize() {
        var _a;
        const serializedData = super.serialize();
        const chart = (_a = this.terceraChartRactive) === null || _a === void 0 ? void 0 : _a.terceraChart;
        serializedData.indicators = chart.SaveIndicators();
        serializedData.overlays = chart.SaveOverlays();
        return serializedData;
    }
    deserialize(serialized) {
        super.deserialize(serialized);
        if (!this.completed) {
            return;
        }
        this.terceraChartRactive.terceraChart.LoadIndicators(serialized.indicators);
        void this.terceraChartRactive.terceraChart.LoadOverlaysAsync(serialized.overlays);
    }
    deserializeOldScheme(serialized) {
        super.deserializeOldScheme(serialized);
        if (!this.completed) {
            return;
        }
        const indicators = !isNullOrUndefined(serialized.xmlSettingsTemplate)
            ? serialized.xmlSettingsTemplate.indicatorsObj
            : {};
        const overlays = !isNullOrUndefined(serialized.xmlSettingsTemplate)
            ? serialized.xmlSettingsTemplate.overlaysObj
            : {};
        this.terceraChartRactive.terceraChart.LoadIndicators(indicators);
        void this.terceraChartRactive.terceraChart.LoadOverlaysAsync(overlays);
    }
    deserializeDelayedData() {
        let deserialized = false;
        this.terceraChartRactive.terceraChart.model.skipRefreshChartInCallback = true;
        // Grid external serialized data
        const chartSerializedSettings = this.get('chartSerializedSettings');
        if (!isNullOrUndefined(chartSerializedSettings)) {
            if (chartSerializedSettings.isOldScheme) {
                this.deserializeOldScheme(chartSerializedSettings.settings);
            }
            else {
                this.deserialize(chartSerializedSettings.settings);
            }
            deserialized = true;
        }
        else {
            deserialized = super.deserializeDelayedData();
        }
        this.terceraChartRactive.terceraChart.model.skipRefreshChartInCallback = false;
        this.terceraChartRactive.terceraChart.chartController.SuspendRefreshChart = false;
        return deserialized;
    }
    // #endregion
    openNewPanel(panelName) {
        const exitFs = document.fullscreenElement ? document.exitFullscreen() : Promise.resolve();
        exitFs.then(() => {
            const accItem = this.get('accountItem');
            const insItem = this.get('instrumentItem');
            MainWindowManager.Factory.addPanel(panelName, null, (panel) => {
                if (!isNullOrUndefined(accItem)) {
                    panel.accountLink_In(accItem.AcctNumber);
                }
                if (!isNullOrUndefined(insItem)) {
                    panel.symbolLink_In(insItem.GetInteriorID());
                }
            });
        });
    }
}
ApplicationPanelWithTable.extendWith(ChartPanel, {
    Name: 'ChartPanel',
    data: function () {
        return {
            showFullscreenModeButton: true,
            isAccountLinkShow: true,
            isSymbolLinkShow: true,
            instrumentItem: null,
            accountItem: null,
            isShowOE: false,
            oeSmall: true,
            showToolsPanel: false,
            showMainToolBarPanel: true,
            showAnalyzeToolsPanel: false,
            showAccountLookUp: true,
            isChartHasMinSize: true,
            tradingLocked: false,
            canFilterByAccount: false,
            showOverlayButton: true,
            dataSourceOpen: false,
            dataSourceVisible: true, // true to enable data source
            dataSourceBottom: 5,
            hasCorrectData: false,
            verticalToolsPanel: true,
            leftOrTopToolsPanel: true,
            addWidgets: true,
            minSizeForToolBars: 200,
            chartIsLoading: false,
            isGoToOnlyDate: false,
            goToDate: null,
            goToBtnTooltip: 'GoToSelector.Btn.tt',
            goToHeader: '',
            isGoToVisible: true,
            isIndicatorLookupOpen: false,
            isOverlayScreenOpen: false,
            isOldScheme: false,
            chartSerializedSettings: null
        };
    },
    computed: {
        terceraChartPanelContext: {
            get: function () {
                return this;
            },
            set: function (value) { }
        },
        autoLoadingNowClass: {
            get: function () {
                return this.get('chartIsLoading') ? 'autoLoadingNow' : '';
            }
        }
    },
    partials: {
        bodyPartial: ChartPanelTemplate
    }
});
